import { t } from "i18next";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { screenUrl } from "@/config/publicConfig";
import { useFetch } from "../../../../hooks/useFetch";
import { useAppStore } from "../../../../stores/RootStore";
import { useScreenStore } from "../../../../stores/ScreenStore";
import { ScreenType } from "../../../../types/types";
import { Button } from "../../../../ui/Button";
import { Input } from "../../../../ui/Input/Input";
import { Label } from "../../../../ui/Label/Label";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { SelectButton } from "../../../../ui/SelectButton/SelectButton";
import { SwitchSM } from "../../../../ui/Switch/Switch";
import MonitorDelete from "../MonitorDelete";

type Props = {
  updateMonitor: Function;
};

const ScreenSettingsTab = ({updateMonitor}:Props) => {
  const {monitor} = useScreenStore();
  const {screens, setScreens} = useAppStore();
  const currentSite = useAppStore((store) => store.currentSite);

  const divisions = useAppStore((store) => store.divisions);
  const divisionsList = divisions?.map((division) => {
    return { value: division.id, label: division.attributes.name };
  });

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { handleSubmit, reset, control } = useForm({
    defaultValues: {
      name: monitor?.attributes.name,
      slug: `/screen/${currentSite?.subdomain}/${monitor?.attributes.slug}`,
      public: monitor?.attributes.public,
      division: monitor?.attributes.division ? divisionsList?.find((division) => division.value == String(monitor?.attributes.division.id)) : null,
    },
  });

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  // Обновление информации об участнике
  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/directories/screens/${monitor?.id}`
  );

  // Баг на бэке с редактированием
  const onSubmit = (formData: any) => {
    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          name: formData.name,
          public: formData.public,
          division_id: formData.division?.value ? formData.division.value : null,
        },
      },
    });
  };

  const editScreen = (itemSaveResponse: { data: unknown }) => {
    const newScreenData = itemSaveResponse.data as unknown as ScreenType;
    const withoutCurrentItem = screens?.filter((screen) => screen.id != monitor?.id)!;
    setScreens([...withoutCurrentItem, newScreenData]);
  };

  useEffect(() => {
    if (!saveResponse) return;
    editScreen(saveResponse);
  }, [saveResponse]);

  const navigate = useNavigate();
  const publicScreenUrl = currentSite?.subdomain && monitor?.attributes.slug
    ? screenUrl(currentSite.subdomain, monitor.attributes.slug)
    : "#";

  return ( 
    <>
      <div className="relative flex flex-col w-full h-full overflow-hidden bg-background px-6">
        <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
          <div className="flex flex-col space-y-1">
            <span className="text-2xl font-semibold">{t('settings')}</span>
          </div>
        </div>
        <ScrollArea>
          <form onSubmit={handleSubmit(onSubmit)} className="px-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col relative space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <div className="font-medium">{t("screen_name")}</div>
                    <Controller
                      name="public"
                      control={control}
                      render={({ field }) => (
                        <div className="relative space-y-2">
                          <div className="flex items-center space-x-2">
                            <Label htmlFor="public">{t("screen_public")}</Label>
                            <SwitchSM checked={field.value} onCheckedChange={field.onChange} id="public" />
                          </div>{" "}
                        </div>
                      )}
                    />
                  </div>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <div className="relative space-y-2">
                        <Input {...field} />
                      </div>
                    )}
                  />
                </div>
                <Controller
                  name="slug"
                  control={control}
                  disabled
                  render={({ field }) => (
                    <div className="flex flex-col relative space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <div className="font-medium">{t("screen_slug")}</div>
                        <a href={publicScreenUrl}></a>
                        <Button
                          variant="outline"
                          size="xs"
                          // onClick={() => navigate(publicScreenUrl, {replace: false})}
                        >
                          <a href={publicScreenUrl}>{t("go_to")}</a>
                        </Button>
                      </div>
                      <Input {...field}/>

                    </div>
                  )}
                />
                <Controller
                  name="division"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="relative space-y-1">
                      <Label>{t("division")}</Label>
                      <SelectButton
                        label={"Выберите подразделение"}
                        searchable={true}
                        options={divisionsList!}
                        value={value ? divisionsList?.find((s) => s.value == String(value.value))! : null}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
                <div className="mt-8 flex w-full justify-between">
                  <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                    {t("delete")}
                  </Button>
                  <Button variant={"default"} size={"sm"} type="submit">
                    {t("save")}
                  </Button>
                </div>
              </div>
          </form>
        </ScrollArea>
      </div>
      {isDeleteOpen && <MonitorDelete isDeleteOpen={isDeleteOpen} closeModal={closeDeleteModal} />}
    </>
  );
};

export default ScreenSettingsTab;
